<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-3">
      <v-icon class="mr-2">mdi-chart-box-outline</v-icon>
      <span class="text-h6 font-weight-bold">Abandonment Breakdown</span>
      <v-spacer></v-spacer>
      <span class="text-caption text-medium-emphasis">
        {{ totalOffers.toLocaleString() }} abandonments in the selected period
      </span>
    </div>

    <v-card v-if="loading" class="pa-8 text-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-card>

    <v-card v-else-if="totalOffers === 0" class="pa-8 text-center text-medium-emphasis">
      No abandonments in the selected period.
    </v-card>

    <v-row v-else>
      <!-- Occasion: Wedding and Birthday called out, every other occasion as context. -->
      <v-col cols="12" md="6">
        <v-card class="h-100">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-party-popper</v-icon>
            By Occasion
          </v-card-title>
          <v-card-subtitle>Which occasions people drop out of</v-card-subtitle>
          <v-card-text>
            <div class="d-flex flex-wrap ga-2 mb-4">
              <div
                v-for="tile in occasionTiles"
                :key="tile.label"
                class="stat-tile flex-grow-1"
              >
                <div class="d-flex align-center">
                  <span class="tile-dot mr-2" :style="{ backgroundColor: tile.color }"></span>
                  <span class="text-caption text-medium-emphasis">{{ tile.label }}</span>
                </div>
                <div class="text-h5 font-weight-bold">{{ tile.count.toLocaleString() }}</div>
                <div class="text-caption text-medium-emphasis">{{ percent(tile.count) }} of all</div>
              </div>
            </div>
            <BarList :rows="occasionRows" :total="totalOffers" label-width="40%" />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Party size: the distribution, then the same buckets against submitted quotes. -->
      <v-col cols="12" md="6">
        <v-card class="h-100">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-account-group</v-icon>
            By Party Size
          </v-card-title>
          <v-card-subtitle>Whether bigger or smaller events drop out</v-card-subtitle>
          <v-card-text>
            <div class="d-flex flex-wrap ga-2 mb-4">
              <div class="stat-tile flex-grow-1">
                <div class="text-caption text-medium-emphasis">Median party</div>
                <div class="text-h5 font-weight-bold">{{ guestStats.median ?? '—' }}</div>
                <div class="text-caption text-medium-emphasis">guests</div>
              </div>
              <div class="stat-tile flex-grow-1">
                <div class="text-caption text-medium-emphasis">Average party</div>
                <div class="text-h5 font-weight-bold">{{ guestStats.average ?? '—' }}</div>
                <div class="text-caption text-medium-emphasis">guests</div>
              </div>
              <div class="stat-tile flex-grow-1">
                <div class="text-caption text-medium-emphasis">Largest</div>
                <div class="text-h5 font-weight-bold">{{ guestStats.maximum ?? '—' }}</div>
                <div class="text-caption text-medium-emphasis">guests</div>
              </div>
            </div>
            <apexchart
              type="bar"
              height="260"
              :options="guestOptions"
              :series="guestSeries"
            />
            <v-table density="compact" class="mt-2 breakdown-table">
              <thead>
                <tr>
                  <th class="text-left">Party size</th>
                  <th class="text-right">Abandoned</th>
                  <th class="text-right">Quotes sent</th>
                  <th class="text-right">Abandon rate</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in guestRows" :key="row.label">
                  <td class="font-weight-medium text-no-wrap">{{ row.label }} guests</td>
                  <td class="text-right">{{ row.offers.toLocaleString() }}</td>
                  <td class="text-right text-medium-emphasis">{{ row.quotes.toLocaleString() }}</td>
                  <td class="text-right font-weight-bold">{{ row.rate }}</td>
                </tr>
              </tbody>
            </v-table>
            <div class="text-caption text-medium-emphasis mt-2">
              Abandon rate = abandonments ÷ (abandonments + quotes sent) for that party size.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Menu: kept in menu order, zeros included, so a single menu can be looked up. -->
      <v-col cols="12" md="6">
        <v-card class="h-100">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-silverware-fork-knife</v-icon>
            By Selected Menu
          </v-card-title>
          <v-card-subtitle>Which menu was on screen when people left</v-card-subtitle>
          <v-card-text>
            <BarList :rows="menuRows" :total="totalOffers" label-width="46%" />
            <div class="text-caption text-medium-emphasis mt-3">
              Offers abandoned before a menu was picked are not counted here.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Delivery type. -->
      <v-col cols="12" md="6">
        <v-card class="h-100">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-truck-delivery-outline</v-icon>
            By Delivery Type
          </v-card-title>
          <v-card-subtitle>On-site, drop off or pickup</v-card-subtitle>
          <v-card-text>
            <BarList :rows="deliveryRows" :total="totalOffers" label-width="34%" />
            <div v-if="unrecordedDelivery > 0" class="text-caption text-medium-emphasis mt-3">
              “Not recorded” covers offers left before a delivery option was chosen.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Day of the week of the event date, Monday first, zeros included. -->
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-calendar-week</v-icon>
            By Day of the Week
          </v-card-title>
          <v-card-subtitle>Which event day was picked when people left</v-card-subtitle>
          <v-card-text>
            <apexchart
              type="bar"
              height="280"
              :options="weekdayOptions"
              :series="weekdaySeries"
            />
            <div v-if="unrecordedWeekday > 0" class="text-caption text-medium-emphasis mt-2">
              {{ unrecordedWeekday.toLocaleString() }} offers abandoned before an event date was picked are not counted here.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import BarList from "./BarList.vue";
import { comebackService } from "../services";
import { formatDelivery } from "../utils";

// Chart palette. Blue carries every plain magnitude bar; orange is the second
// accent, used only where two categories are being told apart (Wedding vs
// Birthday); grey is the de-emphasis tone for context bars and "not recorded".
const ACCENT = "#2a78d6";
const ACCENT_ALT = "#eb6834";
const MUTED = "#898781";
const INK = "#0b0b0b";
const GRID = "#e1e0d9";

export default {
  name: "ComebackBreakdownStats",
  components: { apexchart: VueApexCharts, BarList },
  props: {
    startDate: { type: [String, Date], default: null },
    endDate: { type: [String, Date], default: null },
  },
  data() {
    return {
      loading: true,
      totalOffers: 0,
      occasions: [],
      delivery: [],
      menus: [],
      guestBuckets: [],
      guestStats: { average: null, median: null, minimum: null, maximum: null },
      weekdays: [],
      unrecordedWeekday: 0,
    };
  },
  computed: {
    // Single key for both bounds so moving the range triggers one reload.
    rangeKey() {
      return `${this.toYmd(this.startDate)}|${this.toYmd(this.endDate)}`;
    },

    occasionRows() {
      return this.occasions.map(row => ({
        label: row.occasion || "Not specified",
        count: row.count,
        color: this.occasionColor(row.occasion),
      }));
    },
    // The three headline numbers the occasion question actually asks for.
    occasionTiles() {
      const wedding = this.occasionCount("Wedding");
      const birthday = this.occasionCount("Birthday");
      return [
        { label: "Wedding", count: wedding, color: ACCENT },
        { label: "Birthday", count: birthday, color: ACCENT_ALT },
        { label: "All other occasions", count: this.totalOffers - wedding - birthday, color: MUTED },
      ];
    },

    deliveryRows() {
      // The three real options always get a row, zero-filled, so the card keeps
      // the same shape whatever the period holds — and they stay in the order
      // the booking widget lists them. Anything unexpected follows, then the
      // offers with nothing recorded.
      const known = ["0", "1", "2"];
      const rows = known.map(code => ({
        label: formatDelivery(code),
        count: this.deliveryCount(code),
        color: ACCENT,
      }));
      this.delivery
        .filter(r => r.code !== null && r.code !== undefined && !known.includes(String(r.code)))
        .forEach(r => rows.push({ label: formatDelivery(r.code), count: r.count, color: ACCENT }));
      if (this.unrecordedDelivery > 0) {
        rows.push({ label: "Not recorded", count: this.unrecordedDelivery, color: MUTED });
      }
      return rows;
    },
    unrecordedDelivery() {
      const row = this.delivery.find(r => r.code === null || r.code === undefined);
      return row ? row.count : 0;
    },

    menuRows() {
      return this.menus.map(row => ({
        label: row.name || `Menu #${row.index + 1}`,
        count: row.count,
        color: ACCENT,
      }));
    },

    guestRows() {
      return this.guestBuckets.map(bucket => {
        const enquiries = bucket.offers + bucket.quotes;
        return {
          label: bucket.label,
          offers: bucket.offers,
          quotes: bucket.quotes,
          rate: enquiries > 0 ? `${((bucket.offers / enquiries) * 100).toFixed(1)}%` : "—",
        };
      });
    },
    guestSeries() {
      return [{ name: "Abandonments", data: this.guestRows.map(r => r.offers) }];
    },
    guestOptions() {
      return this.columnChartOptions(
        this.guestRows.map(r => r.label),
        this.guestRows.map(r => r.offers),
        "Guests",
      );
    },

    weekdaySeries() {
      return [{ name: "Abandonments", data: this.weekdays.map(r => r.count) }];
    },
    weekdayOptions() {
      return this.columnChartOptions(
        this.weekdays.map(r => r.day),
        this.weekdays.map(r => r.count),
        "Event day",
      );
    },
  },
  watch: {
    rangeKey() { this.loadData(); },
  },
  methods: {
    // Shared look for the single-series column charts: blue bars, count and
    // share of all abandonments on top, headroom so those labels never clip.
    columnChartOptions(categories, values, axisTitle) {
      const headroom = this.niceCeil(Math.max(...values, 1) * 1.25);
      return {
        chart: {
          type: "bar",
          fontFamily: "inherit",
          toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
        },
        plotOptions: {
          bar: {
            columnWidth: "55%",
            borderRadius: 4,
            borderRadiusApplication: "end",
            dataLabels: { position: "top" },
          },
        },
        colors: [ACCENT],
        dataLabels: {
          enabled: true,
          offsetY: -20,
          formatter: val => `${val} · ${this.percent(val)}`,
          style: { fontSize: "12px", fontWeight: 600, colors: values.map(() => INK) },
          // Apex draws a white pill behind labels by default, which hides the
          // ink colour it was given.
          background: { enabled: false },
        },
        xaxis: {
          categories,
          title: { text: axisTitle, style: { color: MUTED, fontWeight: 500 } },
          labels: { style: { colors: MUTED } },
          axisBorder: { color: GRID },
          axisTicks: { color: GRID },
        },
        yaxis: {
          min: 0,
          max: headroom,
          tickAmount: 4,
          decimalsInFloat: 0,
          labels: { style: { colors: MUTED }, formatter: val => Math.round(val) },
        },
        grid: { borderColor: GRID, xaxis: { lines: { show: false } } },
        legend: { show: false },
        tooltip: {
          y: { formatter: val => `${val.toLocaleString()} abandonments (${this.percent(val)} of all)` },
        },
      };
    },
    toYmd(d) {
      if (!d) return undefined;
      const dt = new Date(d);
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const day = String(dt.getDate()).padStart(2, "0");
      return `${dt.getFullYear()}-${m}-${day}`;
    },
    // Round up to a 1/2/4/5/10 x 10^k step so the axis lands on round ticks.
    niceCeil(value) {
      if (!(value > 0)) return 1;
      const power = Math.pow(10, Math.floor(Math.log10(value)));
      const scaled = value / power;
      const step = [1, 2, 4, 5, 10].find(s => scaled <= s) || 10;
      return step * power;
    },
    percent(count) {
      if (!this.totalOffers) return "0%";
      return `${((count / this.totalOffers) * 100).toFixed(1)}%`;
    },
    occasionCount(name) {
      const row = this.occasions.find(r => r.occasion === name);
      return row ? row.count : 0;
    },
    occasionColor(occasion) {
      if (occasion === "Wedding") return ACCENT;
      if (occasion === "Birthday") return ACCENT_ALT;
      return MUTED;
    },
    deliveryCount(code) {
      const row = this.delivery.find(r => String(r.code) === code);
      return row ? row.count : 0;
    },
    async loadData() {
      this.loading = true;
      try {
        const response = await comebackService.getBreakdowns({
          startDate: this.toYmd(this.startDate),
          endDate: this.toYmd(this.endDate),
        });
        if (response?.success && response.data) {
          const data = response.data;
          this.totalOffers = data.totalOffers || 0;
          this.occasions = data.occasions || [];
          this.delivery = data.delivery || [];
          this.menus = data.menus || [];
          this.guestBuckets = data.guests?.buckets || [];
          this.guestStats = data.guests?.stats || { average: null, median: null, minimum: null, maximum: null };
          this.weekdays = data.weekdays?.days || [];
          this.unrecordedWeekday = data.weekdays?.unrecorded || 0;
        }
      } catch (e) {
        console.error("Failed to load comeback breakdowns", e);
        this.totalOffers = 0;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.stat-tile {
  min-width: 110px;
  padding: 10px 12px;
  border: 1px solid rgba(11, 11, 11, 0.1);
  border-radius: 8px;
}

.tile-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.breakdown-table :deep(th) {
  white-space: nowrap;
}
</style>
